import React, { FC } from "react";

import { svgPathData } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { defineMessages, useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Modal } from "@mapmycustomers/ui";

import upsellImg from "@app/assets/dashboard/mileage-tracking-upsell.svg";
import colors from "@app/styles/_colors.module.scss";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

import styles from "./MileageTrackingUpsellModal.module.scss";

const messages = defineMessages({
  cancel: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.cancel",
    defaultMessage: "Not Now",
    description: "Cancel button in Mileage Tracker upsell modal in the Add Card wizard",
  },
  managerLine1: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.managerLine1",
    defaultMessage: "Track drives automatically",
    description:
      "Line 1 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Manager",
  },
  managerLine2: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.managerLine2",
    defaultMessage: "Classify drives based on working hours",
    description:
      "Line 2 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Manager",
  },
  managerLine3: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.managerLine3",
    defaultMessage: "Access live reports on all drives",
    description:
      "Line 3 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Manager",
  },
  managerLine4: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.managerLine4",
    defaultMessage: "Share a detailed report instantly",
    description:
      "Line 4 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Manager",
  },
  ok: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.ok",
    defaultMessage: "I’m Interested",
    description: "Ok button in Mileage Tracker upsell modal in the Add Card wizard",
  },
  ownerLine1: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.ownerLine1",
    defaultMessage: "Track your team's driving time effortlessly",
    description:
      "Line 1 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Owner",
  },
  ownerLine2: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.ownerLine2",
    defaultMessage: "Determine whether a drive was auto-tracked or manually logged",
    description:
      "Line 2 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Owner",
  },
  ownerLine3: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.ownerLine3",
    defaultMessage: "Monitor business drives in real time",
    description:
      "Line 3 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Owner",
  },
  ownerLine4: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.ownerLine4",
    defaultMessage: "Export comprehensive reports in seconds",
    description:
      "Line 4 in bullet-points in the Mileage Tracker upsell modal in the Add Card wizard for Owner",
  },
  title: {
    id: "dashboard.addEditCard.preview.mileageTracking.upsellModal.title",
    defaultMessage: "Unlock the Drive Time Tracker with Mileage Tracker",
    description: "Title of the Mileage Tracker upsell modal in the Add Card wizard",
  },
});

const checkIconSvg = `<svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="${colors.primary100}" d="${svgPathData}"/></svg>`;

export interface MileageTrackingUpsellModalProps {
  isOwner?: boolean;
}

const MileageTrackingUpsellModal: FC<MileageTrackingUpsellModalProps> = ({ isOwner }) => {
  const intl = useIntl();

  const [open, , hide] = useBoolean(true);

  const handleOk = () => {
    window.open("https://share.hsforms.com/1VRulM5fhTvmNRxQAMKtaQg3is1h", "_blank");
    hide();
  };

  return (
    <Modal
      cancelText={intl.formatMessage(messages.cancel)}
      closable={false}
      okText={intl.formatMessage(messages.ok)}
      onCancel={hide}
      onOk={handleOk}
      open={open}
      width={"24rem"}
    >
      <div className={styles.header}>
        <img alt="Mileage Tracking preview" className={styles.image} src={upsellImg} />
        <div className={styles.title}>{intl.formatMessage(messages.title)}</div>
      </div>
      <ul
        className={styles.points}
        style={{ listStyleImage: `url(${svgToDataUrl(checkIconSvg)})` }}
      >
        {isOwner ? (
          <>
            <li>
              <span>{intl.formatMessage(messages.ownerLine1)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.ownerLine2)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.ownerLine3)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.ownerLine4)}</span>
            </li>
          </>
        ) : (
          <>
            <li>
              <span>{intl.formatMessage(messages.managerLine1)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.managerLine2)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.managerLine3)}</span>
            </li>
            <li>
              <span>{intl.formatMessage(messages.managerLine4)}</span>
            </li>
          </>
        )}
      </ul>
    </Modal>
  );
};

export default MileageTrackingUpsellModal;
